import { Diff } from 'lucide-react';
import { useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useRecoilValue } from 'recoil';
import ToolbarButton from '~/components/toolbar/shared/ToolbarButton';
import { STRUCTURE_SET_NOT_SHOWN_HINT_ID } from '~/hooks/target/structureSetNotShown';
import { buildToolTooltip } from '~/components/toolbar/shared/toolTooltip';
import ToolbarPopover from '~/components/toolbar/shared/ToolbarPopover';
import { useActiveEditTarget } from '~/hooks/target/useActiveEditTarget';
import { differenceReferenceModeState } from '~/state/atoms/toolsAtoms';
import DifferenceOverlayOptions from './DifferenceOverlayOptions';

/**
 * Standalone "Differences" viewer tool: a toolbar button that opens a popover
 * to pick which reference the difference overlay highlights the selected
 * structure against (previous slice / before last op / original AI), or 'off'.
 * It is reachable in any mode, not only while a draw tool is active, so edits
 * can be reviewed at any point.
 *
 * The overlay is a display aid drawn by the always-mounted
 * {@link useReferenceDiff} on whichever surface is open; this tool only
 * writes {@link differenceReferenceModeState}. The button is highlighted
 * while a reference is active and the overlay can be drawn: a mode that is
 * on while the structure set sits on no view is not lit, and the tooltip
 * says why, so an absent overlay never reads as a clean match. The options
 * open in the toolbar's shared popover beneath it.
 */
export default function DifferenceOverlayTool() {
    const intl = useIntl();
    const anchorRef = useRef<HTMLElement | null>(null);
    const [open, setOpen] = useState(false);
    const mode = useRecoilValue(differenceReferenceModeState);
    const { environment } = useActiveEditTarget();
    const isActive = mode !== 'off';
    const isDrawn = isActive && environment !== undefined;

    return (
        <>
            <ToolbarButton
                icon={<Diff />}
                tooltip={buildToolTooltip(
                    intl,
                    intl.formatMessage({
                        id: 'toolbar.navigation.difference.tooltip',
                    }),
                    isActive && !isDrawn
                        ? intl.formatMessage({
                              id: STRUCTURE_SET_NOT_SHOWN_HINT_ID,
                          })
                        : undefined
                )}
                selected={isDrawn || open}
                ariaPressed={isDrawn}
                anchorRef={anchorRef}
                onClick={() => setOpen((previous) => !previous)}
                data-testid="tool-difference-overlay"
            />
            <ToolbarPopover
                open={open}
                anchorEl={anchorRef.current}
                onClose={() => setOpen(false)}
            >
                <DifferenceOverlayOptions />
            </ToolbarPopover>
        </>
    );
}
